import { useState, useEffect, useCallback } from 'react';
import { GiftCard, CreateGiftCardData, giftCardsApi } from '../../services/api';
import { useGiftCards } from './hooks/useGiftCards';
import { GiftCardForm } from './GiftCardForm';
import { GiftCardTable } from './GiftCardTable';
import { GiftCardTransactions } from './GiftCardTransactions';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/useToast';
import { createErrorToast, createSuccessToast } from '../../utils/errorHandler';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { downloadCertificateImage } from '@/utils/generateCertificateImage';
import { CheckSquare, Square, Download, Trash2 } from 'lucide-react';

function GiftCardsManager() {
  const { giftCards, loading, error, pagination, loadGiftCards, createGiftCard, updateGiftCard, deleteGiftCard, getStatusLabel, getStatusClass } = useGiftCards();
  const { showToast } = useToast();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCard, setEditingCard] = useState<GiftCard | null>(null);
  const [viewingTransactions, setViewingTransactions] = useState<GiftCard | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const loadData = useCallback(() => {
    loadGiftCards(currentPage, 50, search || undefined, statusFilter === 'all' ? undefined : statusFilter, sortBy, sortOrder);
  }, [loadGiftCards, currentPage, search, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Сброс на первую страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, sortBy, sortOrder]);

  const handleCreate = async (data: CreateGiftCardData) => {
    try {
      await createGiftCard(data);
      setShowCreateForm(false);
      const toast = createSuccessToast('Сертификат успешно создан');
      showToast(toast.message, toast.type, toast.duration);
    } catch (err) {
      const toast = createErrorToast(err);
      showToast(toast.message, toast.type, toast.duration);
    }
  };

  const handleUpdate = async (id: string, data: { name?: string; expireDate?: string | null; status?: string; currentBalance?: number }) => {
    try {
      await updateGiftCard(id, data);
      setEditingCard(null);
      const toast = createSuccessToast('Сертификат успешно обновлен');
      showToast(toast.message, toast.type, toast.duration);
    } catch (err) {
      const toast = createErrorToast(err);
      showToast(toast.message, toast.type, toast.duration);
    }
  };

  const handleViewTransactions = (card: GiftCard) => {
    setViewingTransactions(card);
  };

  const handleToggleBulkMode = () => {
    setIsBulkMode(prev => !prev);
    setSelectedIds(new Set());
  };

  const handleSelectCard = (id: string, selected: boolean) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedIds(new Set(giftCards.map(card => card.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) {
      showToast('Выберите сертификаты для скачивания', 'error');
      return;
    }

    const selectedCards = giftCards.filter(card => selectedIds.has(card.id));
    let successCount = 0;
    let errorCount = 0;

    for (const card of selectedCards) {
      try {
        const initialBalance = parseFloat(card.initialBalance);
        await downloadCertificateImage(card.name, initialBalance);
        try {
          await giftCardsApi.logDownload(card.id);
        } catch (logError) {
          console.error('Ошибка при логировании скачивания:', logError);
        }
        successCount++;
      } catch (error) {
        console.error(`Ошибка при скачивании сертификата ${card.name}:`, error);
        errorCount++;
      }
    }

    if (errorCount === 0) {
      showToast(`Успешно скачано сертификатов: ${successCount}`, 'success');
    } else {
      showToast(`Скачано: ${successCount}, ошибок: ${errorCount}`, 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      showToast('Выберите сертификаты для удаления', 'error');
      return;
    }

    if (!confirm(`Вы уверены, что хотите удалить ${selectedIds.size} сертификат(ов)?`)) {
      return;
    }

    const idsToDelete = Array.from(selectedIds);
    let successCount = 0;
    let errorCount = 0;

    for (const id of idsToDelete) {
      try {
        await deleteGiftCard(id);
        successCount++;
      } catch (err) {
        console.error(`Ошибка при удалении сертификата ${id}:`, err);
        errorCount++;
      }
    }

    setSelectedIds(new Set());
    setIsBulkMode(false);

    if (errorCount === 0) {
      const toast = createSuccessToast(`Успешно удалено сертификатов: ${successCount}`);
      showToast(toast.message, toast.type, toast.duration);
    } else {
      const toast = createErrorToast(new Error(`Удалено: ${successCount}, ошибок: ${errorCount}`));
      showToast(toast.message, toast.type, toast.duration);
    }
  };

  return (
    <div className="bg-card rounded-lg p-6 w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-foreground m-0">Управление подарочными сертификатами</h1>
        <Button onClick={() => setShowCreateForm(true)}>
          Создать сертификат
        </Button>
      </div>


      {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}

      {showCreateForm && (
        <GiftCardForm
          onSave={handleCreate}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {editingCard && (
        <GiftCardForm
          card={editingCard}
          onSave={(data) => handleUpdate(editingCard.id, {
            name: data.name,
            expireDate: data.expireDate || null,
            currentBalance: data.currentBalance,
          })}
          onCancel={() => setEditingCard(null)}
        />
      )}

      {/* Поиск и фильтры */}
      <div className="flex gap-2.5 flex-wrap items-center mb-5">
        <Input
          type="text"
          placeholder="Поиск по номеру..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px]"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Все статусы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="CAN_BE_USED">Не использован</SelectItem>
            <SelectItem value="PARTIALLY_REDEEMED">Частично использован</SelectItem>
            <SelectItem value="EXPIRED">Истек срок действия</SelectItem>
            <SelectItem value="REDEEMED">Полностью использован</SelectItem>
            <SelectItem value="INVALID">Недействителен</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Сортировка" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="createdAt">По дате создания</SelectItem>
            <SelectItem value="name">По номеру</SelectItem>
            <SelectItem value="expireDate">По сроку действия</SelectItem>
            <SelectItem value="currentBalance">По балансу</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
        >
          {sortOrder === 'asc' ? '↑' : '↓'}
        </Button>
        <Button
          variant={isBulkMode ? "default" : "outline"}
          onClick={handleToggleBulkMode}
        >
          {isBulkMode ? (
            <>
              <CheckSquare className="h-4 w-4 mr-2" />
              Выйти
            </>
          ) : (
            <>
              <Square className="h-4 w-4 mr-2" />
              Выбрать
            </>
          )}
        </Button>
        {isBulkMode && selectedIds.size > 0 && (
          <>
            <Button
              variant="outline"
              onClick={handleBulkDownload}
            >
              <Download className="h-4 w-4 mr-2" />
              Скачать ({selectedIds.size})
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Удалить ({selectedIds.size})
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="text-center py-10 text-muted-foreground">Загрузка...</div>
      ) : (
        <div>
          <GiftCardTable
            giftCards={giftCards}
            onEdit={setEditingCard}
            onViewTransactions={handleViewTransactions}
            getStatusLabel={getStatusLabel}
            getStatusClass={getStatusClass}
            isBulkMode={isBulkMode}
            selectedIds={selectedIds}
            onSelect={handleSelectCard}
            onSelectAll={handleSelectAll}
          />
          {pagination.totalPages > 1 && (
            <div className="flex gap-2.5 items-center justify-center mt-5">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Назад
              </Button>
              <span className="text-sm text-muted-foreground">
                Страница {pagination.page} из {pagination.totalPages} (всего {pagination.total})
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={currentPage >= pagination.totalPages}
              >
                Вперед
              </Button>
            </div>
          )}
        </div>
      )}

      {viewingTransactions && (
        <GiftCardTransactions
          cardId={viewingTransactions.id}
          cardName={viewingTransactions.name}
          isOpen={!!viewingTransactions}
          onClose={() => setViewingTransactions(null)}
        />
      )}
    </div>
  );
}

export default GiftCardsManager;

